import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('website_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }
    const formData = new FormData(form);
    formData.delete('website_alt');
    try {
      const res = await fetch('https://readdy.ai/api/form/d9omunqftmtnfcfh0a40', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText); } catch { /* raw text */ }
      if (res.ok && parsed && (parsed as Record<string, unknown>).code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = (parsed && ((parsed as Record<string, unknown>).meta as Record<string, unknown>)?.message) ||
          (parsed && (parsed as Record<string, unknown>).message) || responseText || '提交失败，请稍后再试';
        setFormError(String(serverMsg));
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后再试');
      setFormStatus('error');
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '喵趣漫画网',
    url: 'https://miaoqucomic.com',
    description: '喵趣漫画网为您汇集韩漫、日漫与国漫免费全集，支持下拉式在线阅读。我们致力于打造最好的免费漫画在线阅读平台。',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contact@miaoqucomic.com',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: '北京',
      addressCountry: 'CN',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          <section className="w-full px-4 md:px-6 py-10 md:py-14">
            <div className="max-w-4xl mx-auto">
              <h1 className="text-2xl md:text-4xl font-heading text-foreground-950 mb-6 text-center">
                关于喵趣漫画网
              </h1>

              <div className="prose max-w-none">
                <div className="mb-10">
                  <h2 className="text-lg md:text-xl font-heading text-foreground-950 mb-4">
                    品牌介绍
                  </h2>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    喵趣漫画网成立于2023年，是一个专注于提供优质漫画阅读体验的在线平台。我们致力于汇集全球热门漫画资源，涵盖<strong>韩漫、日漫、国漫</strong>等多种类型，为漫画爱好者打造一个<strong>免费、便捷、流畅</strong>的在线阅读空间。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    平台采用独特的<strong>下拉式阅读</strong>模式，彻底告别传统翻页阅读的繁琐体验。用户只需轻轻滑动，即可一口气看完热门漫画全集，享受沉浸式的漫画阅读乐趣。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    喵趣漫画网秉持"让每个人都能享受漫画的快乐"的理念，坚持<strong>完全免费</strong>的运营模式，不设VIP限制，不收取任何费用。我们相信，好的漫画应该被更多人看到，优质的内容不应被付费墙阻隔。
                  </p>
                </div>

                <div className="mb-10">
                  <h2 className="text-lg md:text-xl font-heading text-foreground-950 mb-4">
                    核心特色
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      { title: '海量漫画资源', desc: '收录韩漫、日漫、国漫等1000+部精选漫画，涵盖玄幻、都市、恋爱、搞笑、悬疑、动作、奇幻等多个分类，满足不同读者的口味偏好。' },
                      { title: '下拉式阅读体验', desc: '创新的下拉式阅读模式，无需点击翻页按钮，流畅滚动一气呵成，在手机和电脑上都能享受极致的阅读舒适度。' },
                      { title: '完全免费开放', desc: '喵趣漫画网所有漫画资源永久免费开放，无需注册登录，无需VIP充值，打开即看，让阅读零门槛。' },
                      { title: '每日同步更新', desc: '热门连载漫画每日跟进更新，韩漫、日漫、国漫的最新章节第一时间呈现，追更党再也不用担心落后。' },
                      { title: '高清画质呈现', desc: '所有漫画采用高清扫描和优化处理，确保每一页漫画画质清晰细腻，细节丰富，还原最佳阅读效果。' },
                      { title: '智能分类推荐', desc: '根据分类标签和热度数据，智能推荐你可能感兴趣的漫画作品，帮你发现更多喜欢的漫画。' },
                    ].map((item, idx) => (
                      <div key={idx} className="flex gap-3 p-4 bg-background-100 rounded-lg border border-background-200/70">
                        <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-full bg-primary-100">
                          <i className="ri-check-line text-primary-500 text-sm"></i>
                        </div>
                        <div>
                          <h3 className="text-sm font-medium text-foreground-900 mb-1">{item.title}</h3>
                          <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mb-10">
                  <h2 className="text-lg md:text-xl font-heading text-foreground-950 mb-4">
                    读者口碑
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      { name: '李同学', city: '北京', text: '喵趣漫画网是我用过最舒服的漫画阅读平台，下拉式阅读设计太贴心了，韩漫资源也很全。' },
                      { name: '张先生', city: '上海', text: '作为资深漫画迷，喵趣漫画网的免费模式真的良心。日漫更新快，国漫推荐也很准。' },
                      { name: '陈小姐', city: '广州', text: '画质清晰，加载速度快，分类清晰。已经推荐给身边所有喜欢看漫画的朋友了！' },
                      { name: '王同学', city: '深圳', text: '每天通勤路上必备，韩漫日漫国漫一网打尽。下拉式阅读特别适合手机党！' },
                    ].map((review, idx) => (
                      <div key={idx} className="p-4 bg-background-100 rounded-lg border border-background-200/70">
                        <div className="flex items-center gap-1 mb-2">
                          {Array.from({ length: 5 }, (_, i) => (
                            <i key={i} className="ri-star-fill text-accent-500 text-xs"></i>
                          ))}
                        </div>
                        <p className="text-sm text-foreground-600 leading-relaxed mb-2">{review.text}</p>
                        <p className="text-xs text-foreground-400">
                          {review.name} · {review.city}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mb-10">
                  <h2 className="text-lg md:text-xl font-heading text-foreground-950 mb-4">
                    发展历程
                  </h2>
                  <div className="relative pl-6 border-l-2 border-primary-200 space-y-6">
                    {[
                      { year: '2023年3月', text: '喵趣漫画网正式上线，首批上线500+部精选漫画，主打韩漫和国漫资源。' },
                      { year: '2023年9月', text: '推出下拉式阅读功能，成为国内首批采用该技术的漫画平台，用户好评如潮。' },
                      { year: '2024年1月', text: '日漫专区重磅上线，引入200+部热门日漫作品，平台月活用户突破50万。' },
                      { year: '2024年6月', text: '完成平台全面升级，优化阅读体验，新增智能推荐算法，漫画资源突破800部。' },
                      { year: '2025年3月', text: '喵趣漫画网月活跃读者突破200万，成为国内领先的免费漫画在线阅读平台。' },
                      { year: '2026年至今', text: '持续拓展漫画资源，韩漫、日漫、国漫作品数量突破1000部，每日为超过500万读者提供优质阅读服务。' },
                    ].map((item, idx) => (
                      <div key={idx} className="relative">
                        <div className="absolute -left-[25px] top-1 w-3 h-3 rounded-full bg-primary-500"></div>
                        <h3 className="text-sm font-medium text-foreground-900">{item.year}</h3>
                        <p className="text-xs text-foreground-500 mt-1 leading-relaxed">{item.text}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h2 className="text-lg md:text-xl font-heading text-foreground-950 mb-4">
                    联系我们
                  </h2>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                          <i className="ri-mail-line text-accent-600"></i>
                        </div>
                        <div>
                          <h3 className="text-sm font-medium text-foreground-900">电子邮箱</h3>
                          <p className="text-xs text-foreground-500">contact@miaoqucomic.com</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                          <i className="ri-customer-service-2-line text-accent-600"></i>
                        </div>
                        <div>
                          <h3 className="text-sm font-medium text-foreground-900">在线客服</h3>
                          <p className="text-xs text-foreground-500">7×24小时在线，随时为您解答问题</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                          <i className="ri-map-pin-line text-accent-600"></i>
                        </div>
                        <div>
                          <h3 className="text-sm font-medium text-foreground-900">公司地址</h3>
                          <p className="text-xs text-foreground-500">中国北京市朝阳区漫画创意产业园A座1208室</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-background-100 rounded-lg border border-background-200/70 p-5">
                      <h3 className="text-sm font-medium text-foreground-900 mb-4">给我们留言</h3>
                      <form
                        id="about-contact-form"
                        data-readdy-form
                        onSubmit={handleSubmit}
                      >
                        <div className="space-y-3">
                          <div>
                            <input
                              type="text"
                              name="name"
                              placeholder="您的姓名"
                              required
                              className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200/70 rounded-md focus:outline-none focus:border-primary-400 transition-colors"
                            />
                          </div>
                          <div>
                            <input
                              type="email"
                              name="email"
                              placeholder="您的邮箱"
                              required
                              className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200/70 rounded-md focus:outline-none focus:border-primary-400 transition-colors"
                            />
                          </div>
                          <div>
                            <textarea
                              name="message"
                              placeholder="请描述您的问题或建议（500字以内）"
                              rows={4}
                              maxLength={500}
                              required
                              className="w-full px-3 py-2 text-sm bg-background-50 border border-background-200/70 rounded-md focus:outline-none focus:border-primary-400 transition-colors resize-none"
                            ></textarea>
                          </div>
                          <div id="website_alt_container">
                            <input
                              type="text"
                              name="website_alt"
                              tabIndex={-1}
                              autoComplete="off"
                              aria-hidden="true"
                              readOnly
                            />
                          </div>
                          <button
                            type="submit"
                            className="w-full py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                          >
                            发送留言
                          </button>
                        </div>
                        {formStatus === 'success' && (
                          <p className="mt-3 text-xs text-green-600 text-center">感谢您的留言，我们会尽快回复！</p>
                        )}
                        {formStatus === 'error' && (
                          <p className="mt-3 text-xs text-red-500 text-center">{formError}</p>
                        )}
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}